import dayjs from "dayjs";
import type { SeriesLineOptions } from "highcharts";

import type { SimplifiedSeries } from "@similarweb/pro-logic-components";
import { shortMonthAndYear } from "@similarweb/pro-logic-components";
import { ChartTooltip } from "@similarweb/ui-components";

import { NOT_AVAILABLE } from "app/filters/const";
import { translate } from "app/filters/i18nFilter";
import { abbrNumberFilter, percentageSignFilter } from "app/filters/ngFilters";
import { type ICurrency, currenciesList } from "app/common/constants/currencies";
import { FlexColumn } from "pro-features/styled components/StyledFlex/src/StyledFlex";

import type { IFormatter } from "../types";

const getRows = (rowData, index: number, selectedCurrency: ICurrency, isPercentage) => {
    const flattenData = [];
    rowData.forEach((singleRowData) => {
        const { name } = singleRowData;
        const volume = singleRowData.data[index]?.y;
        const prevVolume = singleRowData.data[index - 1]?.y;
        const change = index === 0 || prevVolume === 0 || !volume ? NOT_AVAILABLE : (volume - prevVolume) / prevVolume;

        flattenData.push({
            domain: {
                label: name,
                backgroundColor: singleRowData.color,
            },
            volume: {
                value: volume,
                selectedCurrency,
                isPercentage,
            },
            change: {
                value: change,
            },
        });
    });
    return flattenData;
};

interface IColumnsDefinitionProps {
    volumeFilter?: IFormatter;
    changeFilter?: IFormatter;
}

const getColumnsDefinition = ({
    // @ts-expect-error FIXME: Argument of type 'number' is not assignable to parameter of type 'boolean'
    volumeFilter = (value) => abbrNumberFilter()(value, 0),
    changeFilter = (value) => percentageSignFilter()(value, 2),
}: IColumnsDefinitionProps = {}) => {
    return [
        {
            key: "label",
            title: translate("common.domain"),
            selector: "domain" as const,
            align: "left",
            size: "2fr",
            render: ({ domain }) => {
                const { label, backgroundColor } = domain;
                return (
                    <FlexColumn gap={"4px"}>
                        <ChartTooltip.LabelCell
                            label={label}
                            backgroundColor={backgroundColor}
                        />
                    </FlexColumn>
                );
            },
        },
        {
            key: "volume",
            title: translate("common.volume"),
            selector: "volume" as const,
            align: "right",
            size: "1fr",
            borderRight: true,
            render: ({ volume }) => {
                const { value, selectedCurrency, isPercentage } = volume;
                const selectedCurrencySymbol = !isPercentage ? selectedCurrency.icon : "";
                return <ChartTooltip.ValueCell value={`${selectedCurrencySymbol}${volumeFilter(value)}`} />;
            },
        },
        {
            key: "change",
            title: translate("website.analysis.search.overview.graph.ppc.point.change.tooltip"),
            selector: "change" as const,
            align: "right",
            size: "1fr",
            render: ({ change }) => {
                const { value } = change;
                if (value === null) {
                    return null;
                }
                return (
                    <ChartTooltip.ChangeCell
                        value={changeFilter(Math.abs(value))}
                        isDecrease={value < 0}
                        unsigned={value === 0 || value === NOT_AVAILABLE}
                    />
                );
            },
        },
    ];
};

const dateFormat = (date) => dayjs.utc(date).format(shortMonthAndYear);

interface ITooltipFormatterProps {
    chartData: SimplifiedSeries<SeriesLineOptions>;
    selectedCurrency: ICurrency;
    volumeFilter?: IFormatter;
    changeFilter?: IFormatter;
    isPercentage?: boolean;
}

export const getTooltipFormatter = ({
    chartData = [],
    selectedCurrency = currenciesList[0],
    volumeFilter,
    changeFilter,
    isPercentage = false,
}: ITooltipFormatterProps) => {
    return function tooltipFormatter() {
        const { index } = this.points[0].point;
        return (
            <ChartTooltip
                data={getRows(chartData, index, selectedCurrency, isPercentage)}
                columnDefinition={getColumnsDefinition({ volumeFilter, changeFilter })}
                width={400}
                title={dateFormat(this.x)}
            />
        );
    };
};
